import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Users, Clock, BookOpen, Home, Calendar, ArrowRight, TrendingUp, Gift, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const [metrics, setMetrics] = useState(null);
  const [donationMetrics, setDonationMetrics] = useState(null);
  const [recentVolunteers, setRecentVolunteers] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const fetchData = async () => {
    try {
      const [metricsRes, volunteersRes, eventsRes, donationsRes] = await Promise.all([
        fetch(`${API}/metrics`, { credentials: 'include' }),
        fetch(`${API}/volunteers?status=Pending`, { credentials: 'include' }),
        fetch(`${API}/events?upcoming=true`, { credentials: 'include' }),
        fetch(`${API}/admin/donations/metrics`, { credentials: 'include' })
      ]);

      if (metricsRes.ok) {
        const metricsData = await metricsRes.json();
        setMetrics(metricsData);
      }
      if (volunteersRes.ok) {
        const volunteersData = await volunteersRes.json();
        setRecentVolunteers(volunteersData.slice(0, 5));
      }
      if (eventsRes.ok) {
        const eventsData = await eventsRes.json();
        setUpcomingEvents(eventsData.slice(0, 5));
      }
      if (donationsRes.ok) {
        const donationsData = await donationsRes.json();
        setDonationMetrics(donationsData);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSyncHours = async () => {
    setSyncing(true);
    try {
      const response = await fetch(`${API}/hours/sync-all`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Hours synced successfully! ${data.volunteers_updated} volunteers updated.`);
        fetchData(); // Refresh metrics
      } else {
        toast.error('Failed to sync hours');
      }
    } catch (error) {
      toast.error('Failed to sync hours');
    } finally {
      setSyncing(false);
    }
  };

  const statCards = [
    { 
      title: 'Total Volunteers', 
      value: metrics?.total_volunteers || 0, 
      icon: Users, 
      color: 'stat-card-volunteers',
      textColor: 'text-white'
    },
    { 
      title: 'Volunteer Hours', 
      value: metrics?.total_hours?.toLocaleString() || 0, 
      icon: Clock, 
      color: 'stat-card-hours',
      textColor: 'text-[#1a3a5c]'
    },
    { 
      title: 'Bookshelves Built', 
      value: metrics?.bookshelves_built || 0, 
      icon: BookOpen, 
      color: 'stat-card-bookshelves',
      textColor: 'text-[#1a3a5c]'
    },
    { 
      title: 'Families Served', 
      value: metrics?.families_served || 0, 
      icon: Home, 
      color: 'stat-card-families',
      textColor: 'text-white'
    },
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-[#5cc8e8] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-[#1a3a5c]" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Dashboard
            </h1>
            <p className="text-slate-500 mt-1">Welcome back! Here's what's happening.</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleSyncHours}
            disabled={syncing}
            className="text-slate-600"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync Hours'}
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat, index) => (
            <Card key={index} className={`${stat.color} border-0 shadow-lg overflow-hidden`} data-testid={`stat-${stat.title.toLowerCase().replace(' ', '-')}`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm ${stat.textColor} opacity-80`}>{stat.title}</p>
                    <p className={`text-3xl font-bold ${stat.textColor} mt-1`} style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {stat.value}
                    </p>
                  </div>
                  <div className={`w-12 h-12 ${stat.textColor} opacity-20`}>
                    <stat.icon className="w-full h-full" />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Donations Quick Stats */}
        {donationMetrics && donationMetrics.total > 0 && (
          <Card className="border-0 shadow-md bg-gradient-to-r from-purple-50 to-purple-100" data-testid="donations-quick-stats">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-semibold text-[#1a3a5c] flex items-center gap-2">
                <Gift className="w-5 h-5 text-purple-600" />
                In-Kind Donations
              </CardTitle>
              <Link to="/admin/donations">
                <Button variant="ghost" size="sm" className="text-purple-600 hover:text-purple-800">
                  View All <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-white rounded-lg">
                  <p className="text-2xl font-bold text-[#1a3a5c]">{donationMetrics.total}</p>
                  <p className="text-xs text-slate-500">Total Donations</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg">
                  <p className="text-2xl font-bold text-yellow-600">{donationMetrics.by_status?.pending || 0}</p>
                  <p className="text-xs text-slate-500">Pending Pickup</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg">
                  <p className="text-2xl font-bold text-green-600">{donationMetrics.by_status?.completed || 0}</p>
                  <p className="text-xs text-slate-500">Completed</p>
                </div>
                <div className="text-center p-3 bg-white rounded-lg">
                  <p className="text-2xl font-bold text-emerald-600">
                    ${donationMetrics.total_estimated_value?.toLocaleString() || 0}
                  </p>
                  <p className="text-xs text-slate-500">Est. Value</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Pending Applications */}
          <Card className="border-0 shadow-md">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-semibold text-[#1a3a5c]">
                Pending Applications
              </CardTitle>
              <Link to="/admin/volunteers">
                <Button variant="ghost" size="sm" className="text-[#5cc8e8] hover:text-[#1a3a5c]">
                  View All <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {recentVolunteers.length > 0 ? (
                <div className="space-y-3">
                  {recentVolunteers.map((volunteer, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#5cc8e8]/10 rounded-full flex items-center justify-center">
                          <span className="text-[#1a3a5c] font-medium">
                            {volunteer.first_name?.[0]}{volunteer.last_name?.[0]}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-[#1a3a5c]">
                            {volunteer.first_name} {volunteer.last_name}
                          </p>
                          <p className="text-sm text-slate-500">{volunteer.email}</p>
                        </div>
                      </div>
                      <span className="status-badge status-pending">Pending</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No pending applications</p>
              )}
            </CardContent>
          </Card>

          {/* Upcoming Events */}
          <Card className="border-0 shadow-md">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg font-semibold text-[#1a3a5c]">
                Upcoming Events
              </CardTitle>
              <Link to="/admin/events">
                <Button variant="ghost" size="sm" className="text-[#5cc8e8] hover:text-[#1a3a5c]">
                  View All <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {upcomingEvents.length > 0 ? (
                <div className="space-y-3">
                  {upcomingEvents.map((event, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#1a3a5c]/10 rounded-lg flex items-center justify-center">
                          <Calendar className="w-5 h-5 text-[#1a3a5c]" />
                        </div>
                        <div>
                          <p className="font-medium text-[#1a3a5c]">{event.title}</p>
                          <p className="text-sm text-slate-500">
                            {new Date(event.date).toLocaleDateString('en-US', { 
                              month: 'short', 
                              day: 'numeric',
                              year: 'numeric'
                            })} at {event.start_time}
                          </p>
                        </div>
                      </div>
                      <span className="text-sm text-slate-500">
                        {event.registered_volunteers?.length || 0}/{event.max_volunteers}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-center py-8">No upcoming events</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#1a3a5c]">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
              <Link to="/admin/volunteers">
                <Button variant="outline" className="w-full justify-start border-2 hover:border-[#5cc8e8] hover:bg-[#5cc8e8]/5" data-testid="quick-action-volunteers">
                  <Users className="w-4 h-4 mr-2 text-[#5cc8e8]" />
                  Manage Volunteers
                </Button>
              </Link>
              <Link to="/admin/events">
                <Button variant="outline" className="w-full justify-start border-2 hover:border-[#5cc8e8] hover:bg-[#5cc8e8]/5" data-testid="quick-action-events">
                  <Calendar className="w-4 h-4 mr-2 text-[#5cc8e8]" />
                  Create Event
                </Button>
              </Link>
              <Link to="/admin/hours">
                <Button variant="outline" className="w-full justify-start border-2 hover:border-[#5cc8e8] hover:bg-[#5cc8e8]/5" data-testid="quick-action-hours">
                  <Clock className="w-4 h-4 mr-2 text-[#5cc8e8]" />
                  Log Hours
                </Button>
              </Link>
              <Link to="/admin/donations">
                <Button variant="outline" className="w-full justify-start border-2 hover:border-purple-400 hover:bg-purple-50" data-testid="quick-action-donations">
                  <Gift className="w-4 h-4 mr-2 text-purple-600" />
                  Manage Donations
                </Button>
              </Link>
              <Link to="/admin/impact">
                <Button variant="outline" className="w-full justify-start border-2 hover:border-[#5cc8e8] hover:bg-[#5cc8e8]/5" data-testid="quick-action-impact">
                  <TrendingUp className="w-4 h-4 mr-2 text-[#5cc8e8]" />
                  View Impact
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
